package br.com.heyagenda.domain.util

object PhoneUtils {

    /**
     * Formats a raw or partially formatted phone number into the standard Brazilian mask:
     * (XX) XXXXX-XXXX for 11 digits (mobile)
     * (XX) XXXX-XXXX for 10 digits (landline)
     * Handles backspacing over mask characters gracefully.
     * Also strips international country code "+55" if pasted.
     */
    fun applyPhoneMask(input: String, previousValue: String = ""): String {
        var digits = input.filter { it.isDigit() }
        val prevDigits = previousValue.filter { it.isDigit() }

        // If user pasted with country code (+55 11 98765-4321), strip +55
        if (digits.length > 11 && digits.startsWith("55")) {
            digits = digits.drop(2)
        }

        // If user pressed backspace and deleted a mask character (e.g. ")", " ", "-"),
        // the digits count remained the same as previous. Drop the last digit to allow backspacing.
        if (input.length < previousValue.length && digits == prevDigits && digits.isNotEmpty()) {
            digits = digits.dropLast(1)
        }

        digits = digits.take(11)

        return formatDigits(digits)
    }

    /**
     * Formats a clean string of digits into (XX) XXXXX-XXXX or (XX) XXXX-XXXX.
     */
    fun formatDigits(digits: String): String {
        var clean = digits.filter { it.isDigit() }
        if (clean.length > 11 && clean.startsWith("55")) {
            clean = clean.drop(2)
        }
        clean = clean.take(11)

        return when {
            clean.isEmpty() -> ""
            clean.length == 1 -> "($clean"
            clean.length == 2 -> "($clean) "
            clean.length in 3..6 -> "(${clean.substring(0, 2)}) ${clean.substring(2)}"
            clean.length in 7..10 -> "(${clean.substring(0, 2)}) ${clean.substring(2, 6)}-${clean.substring(6)}"
            else -> "(${clean.substring(0, 2)}) ${clean.substring(2, 7)}-${clean.substring(7)}"
        }
    }

    /**
     * Strips all non-digit characters for saving/sending to WhatsApp API.
     */
    fun unmask(phone: String?): String {
        if (phone.isNullOrBlank()) return ""
        return phone.filter { it.isDigit() }
    }
}
